import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AudioWaveform, ChevronDown, ChevronLeft, ChevronUp, Heart, ListMusic, Music2, Play, Plus, Search, Trash2 } from 'lucide-react';
import { formatKeyShort, getCamelotKey, parseTrackTitle } from '@meradio/music-utils';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { brand } from '@/theme/colors';
import { setQueue, playVideo } from '@/features/player/playerSlice';
import { Button, Input, Select, IconButton, Tooltip } from '@/components/ui';
import { analyzeVideo } from '@/services/audio.api';
import { getVideos, VideoWithMetadata } from '@/services/videos.api';
import { Crate, CrateVideo, getCrates, getCrate } from '@/services/crates.api';
import {
  DjSet,
  SetEntry,
  SetVideo,
  SuggestedTrack,
  SetSuggestions,
  getSet,
  addEntryToSet,
  removeEntryFromSet,
  reorderSet,
  getSetSuggestions,
} from '@/services/sets.api';
import {
  formatSetDuration,
  formatTrackDuration,
  setDurationRatio,
  isSetDurationDanger,
  SET_MAX_DURATION_LABEL,
} from './setDuration';

const SET_FULL_MESSAGE = 'Set is full — 3 hours is the limit';

const toPlayerVideo = (video: SetVideo) => ({
  ...video,
  channelId: video.channel?.channelId || video.channel?.id || '',
  channelTitle: video.channel?.name || '',
  publishedAt: video.publishedAt,
  dateAdded: video.publishedAt,
  createdAt: video.publishedAt,
  updatedAt: video.publishedAt,
});

const trackBpm = (track: { audioFeatures?: { tempo: number } | null }): number | null =>
  track.audioFeatures ? Math.round(track.audioFeatures.tempo) : null;

const trackKey = (track: { audioFeatures?: { key: number; mode: number } | null }): string | null =>
  track.audioFeatures ? formatKeyShort(track.audioFeatures.key, track.audioFeatures.mode) : null;

const trackCamelot = (track: { audioFeatures?: { key: number; mode: number } | null }): string | null =>
  track.audioFeatures ? getCamelotKey(track.audioFeatures.key, track.audioFeatures.mode) : null;

const SetBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const currentVideoId = useAppSelector(state => state.player.currentVideo?.videoId);
  const isPlaying = useAppSelector(state => state.player.isPlaying);

  const [set, setSet] = useState<DjSet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [suggestions, setSuggestions] = useState<SetSuggestions>({ compatible: [], wildcards: [] });
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<VideoWithMetadata[]>([]);
  const [crates, setCrates] = useState<Crate[]>([]);
  const [selectedCrateId, setSelectedCrateId] = useState('');
  const [crateVideos, setCrateVideos] = useState<CrateVideo[]>([]);

  const entries = useMemo<SetEntry[]>(
    () => [...(set?.entries ?? [])].sort((a, b) => a.position - b.position),
    [set]
  );

  const orderedVideos = useMemo(() => entries.map(entry => entry.video), [entries]);

  const tailVideoId = useMemo(
    () => (entries.length > 0 ? entries[entries.length - 1].video.id : null),
    [entries]
  );

  const excludeKey = useMemo(() => entries.map(entry => entry.video.id).join(','), [entries]);

  const tailBpm = useMemo(
    () => (entries.length > 0 ? trackBpm(entries[entries.length - 1].video) : null),
    [entries]
  );

  const totalDuration = set?.totalDurationSeconds ?? 0;
  const durationDanger = isSetDurationDanger(totalDuration);
  const durationRatio = setDurationRatio(totalDuration);

  const loadSet = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      setSet(await getSet(id));
    } catch {
      console.error('Failed to load set');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const loadCrates = useCallback(async () => {
    try {
      setCrates(await getCrates());
    } catch {
      setCrates([]);
    }
  }, []);

  const handleAdd = useCallback(
    async (videoId: string) => {
      if (!id) return;
      try {
        setSet(await addEntryToSet(id, videoId));
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        if (/full|limit|3 hours|10800/i.test(message)) {
          console.error(SET_FULL_MESSAGE);
        } else {
          console.error('Failed to add to set');
        }
      }
    },
    [id]
  );

  const handleRemove = useCallback(
    async (videoId: string) => {
      if (!id) return;
      try {
        setSet(await removeEntryFromSet(id, videoId));
      } catch {
        console.error('Failed to remove from set');
      }
    },
    [id]
  );

  const handleMove = useCallback(
    async (index: number, direction: -1 | 1) => {
      if (!id) return;
      const target = index + direction;
      if (target < 0 || target >= entries.length) return;
      const order = entries.map(entry => entry.video.id);
      [order[index], order[target]] = [order[target], order[index]];
      try {
        setSet(await reorderSet(id, order));
      } catch {
        console.error('Failed to reorder set');
      }
    },
    [id, entries]
  );

  const handlePlayFrom = useCallback(
    (index: number) => {
      if (orderedVideos.length === 0) return;
      const playable = orderedVideos.map(toPlayerVideo);
      dispatch(setQueue(playable as any));
      dispatch(playVideo(playable[index] as any));
    },
    [orderedVideos, dispatch]
  );

  const handlePlaySet = useCallback(() => handlePlayFrom(0), [handlePlayFrom]);

  const handleAnalyze = useCallback(async (videoId: string) => {
    try {
      await analyzeVideo(videoId);
    } catch {
      console.error('Failed to queue analysis');
    }
  }, []);

  const handleSelectCrate = useCallback(async (crateId: string) => {
    setSelectedCrateId(crateId);
    if (!crateId) {
      setCrateVideos([]);
      return;
    }
    try {
      const crate = await getCrate(crateId);
      setCrateVideos(crate.videos ?? []);
    } catch {
      console.error('Failed to load crate');
      setCrateVideos([]);
    }
  }, []);

  useEffect(() => {
    loadSet();
  }, [loadSet]);

  useEffect(() => {
    loadCrates();
  }, [loadCrates]);

  useEffect(() => {
    if (!tailVideoId) {
      setSuggestions({ compatible: [], wildcards: [] });
      return;
    }
    let cancelled = false;
    setIsSuggesting(true);
    getSetSuggestions({ videoId: tailVideoId, exclude: excludeKey ? excludeKey.split(',') : [] })
      .then(result => {
        if (!cancelled) setSuggestions(result);
      })
      .catch(() => {
        if (!cancelled) setSuggestions({ compatible: [], wildcards: [] });
      })
      .finally(() => {
        if (!cancelled) setIsSuggesting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tailVideoId, excludeKey]);

  useEffect(() => {
    const query = searchQuery.trim().toLowerCase();
    if (query.length === 0) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await getVideos({ limit: 100, sortBy: 'recent' });
        const matches = (response.data ?? [])
          .filter(video => video.title.toLowerCase().includes(query))
          .slice(0, 20);
        if (!cancelled) setSearchResults(matches);
      } catch {
        if (!cancelled) setSearchResults([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery]);

  const pickerResults = useMemo(() => {
    if (searchQuery.trim().length > 0) {
      return searchResults.map(video => ({ id: video.id, title: video.title, thumbnailUrl: video.thumbnailUrl }));
    }
    if (selectedCrateId) {
      return crateVideos.map(video => ({ id: video.id, title: video.title, thumbnailUrl: video.thumbnailUrl }));
    }
    return [];
  }, [searchQuery, searchResults, selectedCrateId, crateVideos]);

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 rounded bg-neutral-300 dark:bg-bg-dark-tertiary" />
          <div className="h-64 rounded-xl bg-neutral-200 dark:bg-bg-dark-secondary" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Link
        to="/sets"
        className="inline-flex items-center gap-1 text-sm text-meradio-primary hover:text-meradio-primary-dark mb-4"
      >
        <ChevronLeft className="w-4 h-4" />
        Sets
      </Link>

      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <ListMusic className="w-7 h-7 text-meradio-primary flex-shrink-0" />
          <h1 className="text-3xl font-bold text-meradio-dark dark:text-text-dark-primary truncate">
            {set?.name}
          </h1>
        </div>
        <Button variant="primary" onClick={handlePlaySet} disabled={orderedVideos.length === 0}>
          <Play className="w-4 h-4 fill-current" />
          Play Set
        </Button>
      </div>

      <div className="mb-6" data-testid="set-duration-meter">
        <div className="flex items-center justify-between text-sm tabular-nums mb-1">
          <span
            className={
              durationDanger
                ? 'text-danger-600 dark:text-danger-400 font-semibold'
                : 'text-meradio-dark-light dark:text-text-dark-secondary'
            }
          >
            {formatSetDuration(totalDuration)} / {SET_MAX_DURATION_LABEL}
          </span>
          <span className="text-meradio-dark-light dark:text-text-dark-tertiary">
            {entries.length} {entries.length === 1 ? 'track' : 'tracks'}
          </span>
        </div>
        <div className="h-2 rounded-full bg-neutral-200 dark:bg-bg-dark-tertiary overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              durationDanger ? 'bg-danger-500' : 'bg-meradio-primary'
            }`}
            style={{ width: `${durationRatio * 100}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-bg-dark-secondary rounded-xl border border-meradio-light dark:border-border-dark overflow-hidden">
            {entries.length === 0 ? (
              <div className="text-center py-16 px-4">
                <ListMusic className="w-12 h-12 text-neutral-300 dark:text-text-dark-tertiary mx-auto mb-3" />
                <p className="text-neutral-500 dark:text-text-dark-secondary">
                  Add tracks from the picker to start sequencing.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-meradio-light dark:divide-border-dark">
                {entries.map((entry, index) => {
                  const video = entry.video;
                  const parsed = parseTrackTitle(video.title);
                  const bpm = trackBpm(video);
                  const key = trackKey(video);
                  const camelot = trackCamelot(video);
                  return (
                    <li
                      key={video.videoId}
                      onClick={() => handlePlayFrom(index)}
                      className="flex items-center gap-3 px-3 py-3 cursor-pointer hover:bg-meradio-light/20 dark:hover:bg-bg-dark-tertiary/50 transition-colors"
                    >
                      <span className="w-6 flex items-center justify-center text-sm text-meradio-dark-light dark:text-text-dark-tertiary tabular-nums">
                        {currentVideoId === video.videoId ? (
                          <span
                            className={`relative block w-5 h-5 rounded-full ${isPlaying ? 'animate-spin-slow' : ''}`}
                            style={{
                              background: `radial-gradient(circle at 30% 30%, ${brand.dark} 0%, ${brand.darkLight} 40%, ${brand.dark} 70%, #000 100%)`,
                            }}
                          >
                            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-meradio-primary rounded-full" />
                          </span>
                        ) : (
                          index + 1
                        )}
                      </span>
                      <div className="w-12 h-12 flex-shrink-0 rounded overflow-hidden bg-meradio-light dark:bg-bg-dark-primary">
                        {video.thumbnailUrl ? (
                          <img src={video.thumbnailUrl} alt={video.title} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Music2 className="w-5 h-5 text-meradio-dark-light dark:text-text-dark-tertiary" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-meradio-dark dark:text-text-dark-primary truncate">
                          {parsed.track}
                        </p>
                        <p className="text-xs text-meradio-dark-light dark:text-text-dark-tertiary truncate">
                          {parsed.artist ? (
                            <Link
                              to={`/artists/${encodeURIComponent(parsed.artist)}`}
                              onClick={(e) => e.stopPropagation()}
                              className="hover:text-meradio-primary hover:underline"
                            >
                              {parsed.artist}
                            </Link>
                          ) : (
                            video.channel?.name || '—'
                          )}
                        </p>
                      </div>
                      <span className="hidden sm:block w-14 text-sm text-meradio-dark dark:text-text-dark-secondary tabular-nums text-right">
                        {bpm ?? '—'}
                      </span>
                      <span className="hidden sm:block w-12 text-sm text-meradio-dark dark:text-text-dark-secondary text-right">
                        {key ?? '—'}
                      </span>
                      <span className="hidden sm:block w-10 text-sm font-medium text-meradio-primary text-right">
                        {camelot ?? ''}
                      </span>
                      <span className="w-12 text-sm text-meradio-dark-light dark:text-text-dark-secondary tabular-nums text-right">
                        {formatTrackDuration(video.duration)}
                      </span>
                      <div className="flex items-center gap-0.5">
                        <Tooltip label="Move up">
                          <IconButton
                            onClick={(e) => { e.stopPropagation(); handleMove(index, -1); }}
                            variant="ghost"
                            size="sm"
                            aria-label="Move up"
                            disabled={index === 0}
                            className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-primary"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip label="Move down">
                          <IconButton
                            onClick={(e) => { e.stopPropagation(); handleMove(index, 1); }}
                            variant="ghost"
                            size="sm"
                            aria-label="Move down"
                            disabled={index === entries.length - 1}
                            className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-primary"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip label="Remove from set">
                          <IconButton
                            onClick={(e) => { e.stopPropagation(); handleRemove(video.id); }}
                            variant="ghost"
                            size="sm"
                            aria-label="Remove from set"
                            className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-danger-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white dark:bg-bg-dark-secondary rounded-xl border border-meradio-light dark:border-border-dark p-4">
            <h2 className="text-sm font-semibold text-meradio-dark dark:text-text-dark-primary uppercase tracking-wider mb-3">
              Add tracks
            </h2>
            <div className="space-y-2 mb-3">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search videos"
                aria-label="Search videos"
                leftIcon={<Search className="w-4 h-4" />}
              />
              <Select
                value={selectedCrateId}
                onChange={(e) => handleSelectCrate(e.target.value)}
                aria-label="Add from crate"
                className="w-full"
              >
                <option value="">From crate…</option>
                {crates.map(crate => (
                  <option key={crate.id} value={crate.id}>
                    {crate.name} ({crate.videoCount})
                  </option>
                ))}
              </Select>
            </div>
            {pickerResults.length > 0 && (
              <ul className="space-y-1 max-h-64 overflow-y-auto">
                {pickerResults.map(item => (
                  <li key={item.id} className="flex items-center gap-2">
                    <div className="w-9 h-9 flex-shrink-0 rounded overflow-hidden bg-meradio-light dark:bg-bg-dark-primary">
                      {item.thumbnailUrl && (
                        <img src={item.thumbnailUrl} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                      )}
                    </div>
                    <p className="flex-1 min-w-0 text-sm text-meradio-dark dark:text-text-dark-secondary truncate">
                      {item.title}
                    </p>
                    <Tooltip label="Add to set">
                      <IconButton
                        onClick={() => handleAdd(item.id)}
                        variant="ghost"
                        size="sm"
                        aria-label={`Add ${item.title}`}
                        className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-primary"
                      >
                        <Plus className="w-4 h-4" />
                      </IconButton>
                    </Tooltip>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-white dark:bg-bg-dark-secondary rounded-xl border border-meradio-light dark:border-border-dark p-4">
            <h2 className="text-sm font-semibold text-meradio-dark dark:text-text-dark-primary uppercase tracking-wider mb-3">
              Mixes well next
            </h2>
            {tailVideoId === null ? (
              <p className="text-sm text-meradio-dark-light dark:text-text-dark-secondary">
                Add a track to see what mixes well next.
              </p>
            ) : isSuggesting ? (
              <p className="text-sm text-meradio-dark-light dark:text-text-dark-secondary">Finding matches…</p>
            ) : suggestions.compatible.length === 0 ? (
              <p className="text-sm text-meradio-dark-light dark:text-text-dark-secondary">
                No compatible tracks found.
              </p>
            ) : (
              <ul className="space-y-2">
                {suggestions.compatible.map((track: SuggestedTrack) => {
                  const bpm = trackBpm(track);
                  const key = trackKey(track);
                  const camelot = trackCamelot(track);
                  const delta = bpm !== null && tailBpm !== null ? bpm - tailBpm : null;
                  return (
                    <li key={track.video.videoId} className="flex items-center gap-2">
                      <div className="w-9 h-9 flex-shrink-0 rounded overflow-hidden bg-meradio-light dark:bg-bg-dark-primary">
                        {track.video.thumbnailUrl && (
                          <img src={track.video.thumbnailUrl} alt={track.video.title} className="w-full h-full object-cover" loading="lazy" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-meradio-dark dark:text-text-dark-primary truncate">
                          {track.fromCollection && (
                            <Heart className="inline w-3 h-3 mr-1 fill-meradio-primary text-meradio-primary" />
                          )}
                          {parseTrackTitle(track.video.title).track}
                        </p>
                        <p className="text-xs text-meradio-dark-light dark:text-text-dark-tertiary tabular-nums">
                          {bpm ?? '—'} BPM
                          {delta !== null && (
                            <span className="ml-1">({delta >= 0 ? `+${delta}` : delta})</span>
                          )}
                          {key && <span className="ml-2">{key}</span>}
                          {camelot && <span className="ml-1 text-meradio-primary font-medium">{camelot}</span>}
                        </p>
                      </div>
                      <Tooltip label="Add to set">
                        <IconButton
                          onClick={() => handleAdd(track.video.id)}
                          variant="ghost"
                          size="sm"
                          aria-label={`Add ${track.video.title}`}
                          className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-primary"
                        >
                          <Plus className="w-4 h-4" />
                        </IconButton>
                      </Tooltip>
                    </li>
                  );
                })}
              </ul>
            )}

            {suggestions.wildcards.length > 0 && (
              <div className="mt-5">
                <h3 className="text-xs font-semibold text-meradio-dark-light dark:text-text-dark-secondary uppercase tracking-wider mb-2">
                  Wildcards
                </h3>
                <ul className="space-y-2">
                  {suggestions.wildcards.map((track: SuggestedTrack) => (
                    <li key={track.video.videoId} className="flex items-center gap-2">
                      <div className="w-9 h-9 flex-shrink-0 rounded overflow-hidden bg-meradio-light dark:bg-bg-dark-primary">
                        {track.video.thumbnailUrl && (
                          <img src={track.video.thumbnailUrl} alt={track.video.title} className="w-full h-full object-cover" loading="lazy" />
                        )}
                      </div>
                      <p className="flex-1 min-w-0 text-sm text-meradio-dark dark:text-text-dark-secondary truncate">
                        {parseTrackTitle(track.video.title).track}
                      </p>
                      <Tooltip label="Analyze BPM and key">
                        <IconButton
                          onClick={() => handleAnalyze(track.video.videoId)}
                          variant="ghost"
                          size="sm"
                          aria-label={`Analyze ${track.video.title}`}
                          className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-primary"
                        >
                          <AudioWaveform className="w-4 h-4" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip label="Add to set">
                        <IconButton
                          onClick={() => handleAdd(track.video.id)}
                          variant="ghost"
                          size="sm"
                          aria-label={`Add ${track.video.title}`}
                          className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-primary"
                        >
                          <Plus className="w-4 h-4" />
                        </IconButton>
                      </Tooltip>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetBuilder;
